import {LZString} from "../../hardware/LZString";
import { aes128 } from "../../hardware/sha256";
import { QiYiCubeConnection } from "./QiYiCubeConnection";

const QIYI_CIC_LIST = [0x0504];
const UUID_SUFFIX = '-0000-1000-8000-00805f9b34fb';
const SERVICE_UUID = '0000fff0' + UUID_SUFFIX;
const CHRCT_UUID_CUBE = '0000fff6' + UUID_SUFFIX;
const ENCRYPTION_KEYS = ['NoDg7ANAjGkEwBYCc0xQnADAVgkzGAzHNAGyRTanQi5QIFyHrjQMQgsC6QA']


export interface BluetoothDeviceWithMAC extends BluetoothDevice {
    mac?: string;
};

/** Iterate over all known GAN cube CICs to find Manufacturer Specific Data */
function getManufacturerDataBytes(manufacturerData: BluetoothManufacturerData | DataView): DataView | undefined {
    // Workaround for Bluefy browser which may return raw DataView directly instead of Map
    if (manufacturerData instanceof DataView) {
        return new DataView(manufacturerData.buffer.slice(2, 11));
    }
    for (var id of QIYI_CIC_LIST) {
        if (manufacturerData.has(id)) {
            return new DataView(manufacturerData.get(id)!.buffer.slice(0, 9));
        }
    }
    return;
}


/** If browser supports Web Bluetooth watchAdvertisements() API, try to retrieve MAC address automatically */
async function autoRetrieveMacAddress(device: BluetoothDevice): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
        if (/^QY-QYSC-.-[0-9A-F]{4}$/.exec(device.name!)) {
            resolve('CC:A3:00:00:' + device.name!.slice(10, 12) + ':' + device.name!.slice(12, 14));
        }
    });
}

/**
 * Type representing function interface to implement custom MAC address provider
 * @param device Current BluetoothDevice selected by user.
 * @param isFallbackCall Flag indicating this is final and last resort call for MAC address.
 *                       If this flag is not set, custom provider can return null instead of MAC,
 *                       in such case library will try to read MAC automatically.
 */
type MacAddressProvider = (device: BluetoothDevice, isFallbackCall?: boolean) => Promise<string | null>;

/**
 * Initiate new connection with the GAN Smart Cube device
 * @param customMacAddressProvider Optional custom provider for cube MAC address
 * @returns Object representing connection API and state
 */
async function connectGanCube(customMacAddressProvider?: MacAddressProvider): Promise<QiYiCubeConnection> {

    // Request user for the bluetooth device (popup selection dialog)
    var device: BluetoothDeviceWithMAC = await navigator.bluetooth.requestDevice(
        {
            filters: [
                { namePrefix: "QY-QYSC" }
            ],
            optionalServices: [SERVICE_UUID],
            optionalManufacturerData: QIYI_CIC_LIST
        }
    );

    // Retrieve cube MAC address needed for key salting
    var mac = customMacAddressProvider && await customMacAddressProvider(device, false)
        || await autoRetrieveMacAddress(device)
        || customMacAddressProvider && await customMacAddressProvider(device, true);

    if (!mac)
        throw new Error('Unable to determine cube MAC address, connection is not possible!');
    device.mac = mac;

    // Create encryption salt from MAC address bytes placed in reverse order
    var salt = new Uint8Array(device.mac.split(/[:-\s]+/).map((c: string) => parseInt(c, 16)).reverse());

    // Connect to GATT and get device primary services
    var gatt = await device.gatt!.connect();
    var services = await gatt.getPrimaryServices();

    var conn: QiYiCubeConnection | null = null;

    // Resolve type of connected cube device and setup appropriate encryption / protocol driver
    for (let service of services) {
        let serviceUUID = SERVICE_UUID;
        //let commandCharacteristic = await service.getCharacteristic(def.GAN_GEN4_COMMAND_CHARACTERISTIC);
        let stateCharacteristic = await service.getCharacteristic(CHRCT_UUID_CUBE);
        let key = ENCRYPTION_KEYS[0];
        let encrypter = aes128(JSON.parse(LZString.decompressFromEncodedURIComponent(key)!));
        //let driver = new GanGen4ProtocolDriver();
        conn = await QiYiCubeConnection.create(device, stateCharacteristic, encrypter);
    }

    if (!conn)
        throw new Error("Can't find target BLE services - wrong or unsupported cube device model");

    return conn;

}


export {
    connectGanCube
};

